/**
 * ConversationFilters — sidebar panel for the Conversations list page
 * (Story 7.10).
 *
 * Responsibilities:
 *   - Local working state, debounced / committed only when Apply is clicked.
 *   - Render status multi-select as togglable chips.
 *   - Campaign select — loads options from `/admin/campaigns` (operator+) via
 *     React Query; degrades gracefully if the endpoint 403s for viewers.
 *   - Date range, phone prefix, content search, clear button.
 *
 * Purely controlled: the parent owns "applied" state + passes it in as
 * `value`; this component emits `onChange` only on Apply/Clear so the list
 * query is not re-fired on every keystroke.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Filter, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError, apiClient } from '@/lib/api';
import type { ConversationStatus } from './StatusBadge';

export interface ConversationFilterValue {
  statuses: ConversationStatus[];
  campaignId: string;
  dateFrom: string;
  dateTo: string;
  phone: string;
  query: string;
}

export const EMPTY_FILTERS: ConversationFilterValue = {
  statuses: [],
  campaignId: '',
  dateFrom: '',
  dateTo: '',
  phone: '',
  query: '',
};

const STATUS_OPTIONS: ConversationStatus[] = [
  'scheduled',
  'awaiting_response',
  'active',
  'scheduled_callback',
  'converted',
  'declined',
  'inactive',
  'closed',
  'error',
  'human_handoff',
];

interface CampaignOption {
  id: string;
  name: string;
}

interface CampaignListResponse {
  data?: CampaignOption[];
  items?: CampaignOption[];
}

/**
 * Load campaign options. Dev A's `/admin/campaigns` route is still in flight
 * on this branch, so we tolerate either `{ data }` or `{ items }` envelopes
 * and gracefully fall back to an empty list on 403/404 so viewers still see
 * the filter panel.
 */
function useCampaignOptions() {
  return useQuery({
    queryKey: ['conversations', 'campaign-options'],
    queryFn: async (): Promise<CampaignOption[]> => {
      try {
        const res = await apiClient.get<CampaignListResponse>('/admin/campaigns?pageSize=100');
        return res.data ?? res.items ?? [];
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return [];
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export function ConversationFilters({
  value,
  onApply,
  onClear,
}: {
  value: ConversationFilterValue;
  onApply: (next: ConversationFilterValue) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = useState<ConversationFilterValue>(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const campaignOptionsQuery = useCampaignOptions();

  function toggleStatus(status: ConversationStatus) {
    setDraft((prev) => {
      const has = prev.statuses.includes(status);
      return {
        ...prev,
        statuses: has ? prev.statuses.filter((s) => s !== status) : [...prev.statuses, status],
      };
    });
  }

  return (
    <Card data-testid="conversation-filters">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">
          <Filter aria-hidden className="mr-2 inline h-4 w-4" />
          Filtros
        </CardTitle>
        <Button
          size="sm"
          variant="ghost"
          type="button"
          onClick={() => {
            setDraft(EMPTY_FILTERS);
            onClear();
          }}
          aria-label="Limpar filtros"
        >
          <X aria-hidden className="mr-1 h-3 w-3" />
          Limpar
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">Status</div>
          <div className="flex flex-wrap gap-1">
            {STATUS_OPTIONS.map((s) => {
              const active = draft.statuses.includes(s);
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleStatus(s)}
                  aria-pressed={active}
                  data-testid={`filter-status-${s}`}
                  className={`rounded-full px-2 py-0.5 text-xs transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'border bg-background text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label
            htmlFor="filter-campaign"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Campanha
          </label>
          <select
            id="filter-campaign"
            aria-label="Filtro campanha"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            value={draft.campaignId}
            onChange={(e) => setDraft({ ...draft, campaignId: e.target.value })}
          >
            <option value="">Todas</option>
            {(campaignOptionsQuery.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label
              htmlFor="filter-date-from"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              De
            </label>
            <input
              id="filter-date-from"
              type="date"
              aria-label="Data inicial"
              className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              value={draft.dateFrom}
              onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value })}
            />
          </div>
          <div>
            <label
              htmlFor="filter-date-to"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Até
            </label>
            <input
              id="filter-date-to"
              type="date"
              aria-label="Data final"
              className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              value={draft.dateTo}
              onChange={(e) => setDraft({ ...draft, dateTo: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="filter-phone"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Telefone (prefixo)
          </label>
          <input
            id="filter-phone"
            type="text"
            placeholder="5511999"
            aria-label="Filtro telefone"
            className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          />
        </div>

        <div>
          <label
            htmlFor="filter-query"
            className="mb-1 block text-xs font-medium text-muted-foreground"
          >
            Busca em mensagens
          </label>
          <input
            id="filter-query"
            type="text"
            placeholder="ex: promoção"
            aria-label="Busca em mensagens"
            className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            value={draft.query}
            onChange={(e) => setDraft({ ...draft, query: e.target.value })}
          />
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            className="flex-1"
            onClick={() => onApply(draft)}
            aria-label="Aplicar filtros"
          >
            Aplicar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Convert a filter value into a URL querystring for `/admin/conversations`. */
export function toQueryString(f: ConversationFilterValue, page: number, pageSize: number): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (f.statuses.length > 0) params.set('status', f.statuses.join(','));
  if (f.campaignId) params.set('campaignId', f.campaignId);
  if (f.dateFrom) params.set('dateFrom', new Date(f.dateFrom).toISOString());
  if (f.dateTo) {
    // Bump to end-of-day so inclusive date filters behave intuitively.
    const end = new Date(f.dateTo);
    end.setUTCHours(23, 59, 59, 999);
    params.set('dateTo', end.toISOString());
  }
  if (f.phone) params.set('phone', f.phone);
  if (f.query) params.set('query', f.query);
  return params.toString();
}
